import { type UseMutationOptions } from "@tanstack/react-query";

import { type ErrorType } from "@vega-api/configs";
import { getErrorMessage } from "@vega-api/custom/utils/errorHandling";
import { useUpdateUserAndPermissions } from "@vega-api/generated/auth-permission-v1/auth-permission-v1";
import { type Permission } from "@vega-api/generated/types/permission";
import { type RoleNew } from "@vega-api/generated/types/roleNew";
import { type UserPermissionUpdateRequestEntry } from "@vega-api/generated/types/userPermissionUpdateRequestEntry";
import { type UsersPermissionUpdateRequestEntries } from "@vega-api/generated/types/usersPermissionUpdateRequestEntries";
import { useStatusPopup } from "@vega-core/hooks/useStatusPopup";
import { m } from "@vega-core/paraglide/messages";

type SetRolesMutationOptions = Omit<
  UseMutationOptions<
    void,
    unknown,
    { data: UsersPermissionUpdateRequestEntries }
  >,
  "mutationFn"
>;

const prepareUserPermissionsPayload = ({
  userId,
  roleId,
  subjectId,
  rolePermissions,
}: {
  userId: string;
  roleId: string;
  subjectId?: string;
  rolePermissions: Permission[];
}): UserPermissionUpdateRequestEntry[] =>
  rolePermissions.map((permission) => ({
    userId,
    roleId,
    subjectId,
    permissionId: permission.id,
    permissionEnabled: permission.enabled,
  }));

export const useSetRoles = () => {
  const { setError } = useStatusPopup();

  const {
    mutate: updateUserAndPermissions,
    isPending: isPendingUpdateUserAndPermissions,
  } = useUpdateUserAndPermissions({
    mutation: {
      onError: (error: unknown) => {
        const { description } = getErrorMessage(error as ErrorType<unknown>, {
          defaultMessage: m.errors_unknown(),
        });
        setError({
          title: m.userManagement_permissions_statusPermissionsChangeError(),
          description,
        });
      },
    },
  });

  const setRole = ({
    role,
    targetUserId,
    subjectId,
    rolePermissions,
    options,
  }: {
    role: RoleNew;
    targetUserId: string;
    subjectId?: string;
    rolePermissions?: Permission[];
    options?: SetRolesMutationOptions;
  }) => {
    const payload = prepareUserPermissionsPayload({
      userId: targetUserId,
      roleId: role.id,
      subjectId: subjectId,
      rolePermissions: rolePermissions ?? role.permissions,
    });

    updateUserAndPermissions({ data: { entries: payload } }, options);
  };

  const setRoleBySubject = ({
    role,
    targetUserId,
    subjectId,
    options,
  }: {
    role: RoleNew;
    targetUserId: string;
    subjectId: string;
    options?: SetRolesMutationOptions;
  }) => {
    setRole({ role, targetUserId, subjectId, options });
  };
  const setRoleBySubjectWithCustomPermissions = ({
    role,
    targetUserId,
    subjectId,
    rolePermissions,
    options,
  }: {
    role: RoleNew;
    targetUserId: string;
    subjectId?: string;
    rolePermissions: Permission[];
    options?: SetRolesMutationOptions;
  }) => {
    setRole({
      role,
      targetUserId,
      subjectId,
      rolePermissions,
      options,
    });
  };

  const setRoleNoSubject = ({
    role,
    targetUserId,
    options,
  }: {
    role: RoleNew;
    targetUserId: string;
    options?: SetRolesMutationOptions;
  }) => {
    setRole({ role, targetUserId, options });
  };

  return {
    setRoleNoSubject,
    setRoleBySubject,
    setRoleBySubjectWithCustomPermissions,
    isPendingUpdateUserAndPermissions,
  };
};
